import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from "react";
import { connect } from "react-redux";
import moment from "moment";
import clsx from "clsx";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import Api from "api/Api";
import Toaster from "components/common/Toaster";
import PageTitle from "components/common/PageTitle";
import UserWrapper from "views/Users/user.style";

const statusOf = d =>
  !d.lastSeenAt ? "never" : d.isOnline ? "online" : "offline";

const FLEET_COLORS = ["#10b981", "#ef4444", "#94a3b8"];
const NET_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#64748b"];
const LOGS_PAGE_SIZE = 40;
const LOGS_MAX = 200;

const SignalBars = ({ type }) => {
  if (!type || type === "NONE") {
    return <span className="gt-monitor-signal--none">No signal</span>;
  }
  const filled = type === "ETHERNET" ? 5 : type === "WIFI" ? 4 : 2;
  return (
    <span className="gt-monitor-signal">
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          className={clsx(
            "gt-monitor-signal__bar",
            i <= filled && "gt-monitor-signal__bar--on"
          )}
          style={{ height: 4 + i * 3 }}
        />
      ))}
      <span className="gt-monitor-signal__type">{type}</span>
    </span>
  );
};

const ScreenPill = ({ state }) => {
  if (!state) return <span className="text-muted">—</span>;
  const map = {
    SCREENSAVER: { cls: "screensaver", label: "Screensaver" },
    QR_DISPLAY: { cls: "qr", label: "QR Display" },
    IDLE: { cls: "idle", label: "Idle" }
  };
  const cfg = map[state] || { cls: "idle", label: state };
  return (
    <span
      className={clsx("gt-monitor-screen", `gt-monitor-screen--${cfg.cls}`)}
    >
      {cfg.label}
    </span>
  );
};

const MonitorStatCard = ({
  label,
  value,
  subtitle,
  subVariant = "ok",
  icon,
  variant = "teal",
  active,
  onClick
}) => (
  <div
    role="button"
    className={clsx(
      "gt-stat-card",
      `gt-stat-card--${variant}`,
      active && "gt-stat-card--active",
      variant === "rose" && active && "gt-stat-card--danger"
    )}
    onClick={onClick}
  >
    {icon && (
      <div className="gt-stat-card__icon">
        <i className={icon} />
      </div>
    )}
    <div className="gt-stat-card__content">
      <div className="gt-stat-card__label">{label}</div>
      <div className="gt-stat-card__value">{value}</div>
      {subtitle && (
        <div
          className={clsx(
            "gt-stat-card__sub",
            `gt-stat-card__sub--${subVariant}`
          )}
        >
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="gt-chart-tooltip">
      {payload[0].name} — {payload[0].value}
    </div>
  );
};

const MonitorDonut = ({
  title,
  subtitle,
  data,
  colors,
  activeFilter,
  onSegmentClick
}) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="gt-chart-card">
      <div className="gt-chart-card__header">
        <h3 className="gt-chart-card__title">{title}</h3>
        {subtitle && <p className="gt-chart-card__subtitle">{subtitle}</p>}
      </div>
      <div className="gt-chart-card__body">
        <div className="gt-chart-card__chart">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={78}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                paddingAngle={2}
                stroke="#fff"
                strokeWidth={2}
                onClick={(_, index) =>
                  onSegmentClick && onSegmentClick(data[index].name)
                }
              >
                {data.map((d, i) => (
                  <Cell
                    key={d.name}
                    fill={colors[i % colors.length]}
                    opacity={activeFilter && activeFilter !== d.name ? 0.25 : 1}
                    style={{ cursor: onSegmentClick ? "pointer" : "default" }}
                  />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="central"
                fill="#0f172a"
                fontSize="22"
                fontWeight="700"
                dy="-6"
              >
                {total}
              </text>
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="central"
                fill="#94a3b8"
                fontSize="11"
                fontWeight="600"
                dy="14"
              >
                TOTAL
              </text>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="gt-monitor-donut-legend">
          {data.map((d, i) => (
            <span
              key={d.name}
              className={clsx(
                "gt-monitor-donut-legend__item",
                activeFilter &&
                  activeFilter !== d.name &&
                  "gt-monitor-donut-legend__item--dim"
              )}
              onClick={() => onSegmentClick && onSegmentClick(d.name)}
            >
              <span
                className="gt-monitor-donut-legend__dot"
                style={{ background: colors[i % colors.length] }}
              />
              <span className="gt-monitor-donut-legend__name">{d.name}</span>
              <span className="gt-monitor-donut-legend__val">{d.value}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const DEVICE_COMMAND_SUCCESS = {
  REMOTE_LOGIN:
    "Remote login started — the tablet should sign in within a few seconds.",
  OPEN_LOGIN:
    "Login screen opened on the tablet with the device email pre-filled.",
  SET_HOME: "Set as home app command sent to the tablet.",
  APPLY_KIOSK: "Kiosk lock command sent to the tablet.",
  APP_UPDATE: "APK update download started on the tablet.",
  REBOOT: "Reboot command sent — the tablet will restart shortly.",
  CAPTURE_SCREEN: "Screen capture requested."
};

const DeviceMonitoring = props => {
  const { isStore } = props;
  const lockedStore =
    props.storeData?.storeName ||
    (props.authData?.adminType === "STORE" ? props.authData?.storeName : null);

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [networkFilter, setNetworkFilter] = useState(null);
  const [storeFilter, setStoreFilter] = useState(lockedStore || "");
  const [sortBy, setSortBy] = useState("status");
  const toaster = useRef();
  const pollRef = useRef(null);

  const [commandBusy, setCommandBusy] = useState(null);
  const [openActionsId, setOpenActionsId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [apkModal, setApkModal] = useState({
    open: false,
    devices: [],
    fileName: "",
    fileSize: 0,
    uploading: false,
    progress: ""
  });
  const apkFileRef = useRef(null);
  const apkInputRef = useRef(null);
  const [commandLogs, setCommandLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsExpanded, setLogsExpanded] = useState(true);
  const [logsLimit, setLogsLimit] = useState(LOGS_PAGE_SIZE);
  const [logsDeviceFilter, setLogsDeviceFilter] = useState("");
  const [screenModal, setScreenModal] = useState(false);
  const [screenCapture, setScreenCapture] = useState({
    device: null,
    requestId: null,
    status: "idle",
    imageUrl: null,
    error: null
  });

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (lockedStore) setStoreFilter(lockedStore);
  }, [lockedStore]);

  const load = useCallback((opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) setLoading(true);
    Api.get("admin/device/monitoring")
      .then(r => {
        setDevices(r.data.data || []);
        setLastRefreshed(new Date());
      })
      .catch(() => {
        // Background polls stay quiet; only surface errors for manual refresh.
        if (!silent && toaster.current) {
          toaster.current.error("Failed to load device status");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  const loadCommandLogs = useCallback(
    (opts = {}) => {
      const silent = !!opts.silent;
      if (!silent) setLogsLoading(true);
      let path = `admin/device/command-logs?limit=${logsLimit}`;
      if (logsDeviceFilter) {
        path += `&deviceTokenId=${encodeURIComponent(logsDeviceFilter)}`;
      }
      Api.get(path)
        .then(r => {
          setCommandLogs(r.data?.data || []);
        })
        .catch(() => {})
        .finally(() => {
          if (!silent) setLogsLoading(false);
        });
    },
    [logsLimit, logsDeviceFilter]
  );

  useEffect(() => {
    load();
    const iv = setInterval(() => load({ silent: true }), 60000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    loadCommandLogs();
    const logsIv = setInterval(() => loadCommandLogs({ silent: true }), 15000);
    return () => clearInterval(logsIv);
  }, [loadCommandLogs]);

  const changeLogsDeviceFilter = value => {
    setLogsDeviceFilter(value);
    setLogsLimit(LOGS_PAGE_SIZE);
  };

  const loadMoreLogs = () => {
    setLogsLimit(prev => Math.min(prev + LOGS_PAGE_SIZE, LOGS_MAX));
  };

  const canLoadMoreLogs =
    commandLogs.length >= logsLimit && logsLimit < LOGS_MAX;

  const closeScreenModal = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setScreenModal(false);
    setScreenCapture({
      device: null,
      requestId: null,
      status: "idle",
      imageUrl: null,
      error: null
    });
  };

  const pollScreenCapture = requestId => {
    if (pollRef.current) clearInterval(pollRef.current);

    const pollOnce = () => {
      Api.get(
        `admin/device/screen-capture/status?requestId=${encodeURIComponent(
          requestId
        )}`
      )
        .then(r => {
          const data = r.data?.data || {};
          if (data.status === "ready" && data.imageUrl) {
            setScreenCapture(prev => ({
              ...prev,
              status: "ready",
              imageUrl: data.imageUrl,
              error: null
            }));
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          } else if (data.status === "failed" || data.status === "expired") {
            setScreenCapture(prev => ({
              ...prev,
              status: data.status,
              error: data.error || data.status
            }));
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          }
        })
        .catch(() => {});
    };

    pollOnce();
    pollRef.current = setInterval(pollOnce, 2000);
    setTimeout(() => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setScreenCapture(prev =>
          prev.status === "pending"
            ? {
                ...prev,
                status: "expired",
                error: "Tablet did not respond in time"
              }
            : prev
        );
      }
    }, 45000);
  };

  useEffect(() => {
    if (!openActionsId) return undefined;
    const close = () => setOpenActionsId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openActionsId]);

  const toggleDeviceSelected = deviceTokenId => {
    setSelectedIds(prev =>
      prev.includes(deviceTokenId)
        ? prev.filter(id => id !== deviceTokenId)
        : [...prev, deviceTokenId]
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const resetApkModal = () => {
    apkFileRef.current = null;
    if (apkInputRef.current) apkInputRef.current.value = "";
    setApkModal({
      open: false,
      devices: [],
      fileName: "",
      fileSize: 0,
      uploading: false,
      progress: ""
    });
  };

  const openApkModal = targetDevices => {
    const online = (targetDevices || []).filter(d => d.isOnline);
    if (online.length === 0) {
      toaster.current &&
        toaster.current.error(
          "No online devices selected. Offline tablets cannot receive updates."
        );
      return;
    }
    setOpenActionsId(null);
    apkFileRef.current = null;
    if (apkInputRef.current) apkInputRef.current.value = "";
    setApkModal({
      open: true,
      devices: online,
      fileName: "",
      fileSize: 0,
      uploading: false,
      progress: ""
    });
  };

  const closeApkModal = () => {
    setApkModal(prev => {
      if (prev.uploading) return prev;
      apkFileRef.current = null;
      if (apkInputRef.current) apkInputRef.current.value = "";
      return {
        open: false,
        devices: [],
        fileName: "",
        fileSize: 0,
        uploading: false,
        progress: ""
      };
    });
  };

  const onApkFileChosen = e => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!file) {
      apkFileRef.current = null;
      setApkModal(prev => ({
        ...prev,
        fileName: "",
        fileSize: 0
      }));
      return;
    }

    const name = file.name || "";
    if (!name.toLowerCase().endsWith(".apk")) {
      apkFileRef.current = null;
      if (apkInputRef.current) apkInputRef.current.value = "";
      setApkModal(prev => ({
        ...prev,
        fileName: "",
        fileSize: 0
      }));
      toaster.current && toaster.current.error("Only .apk files are allowed.");
      return;
    }

    // Keep File in a ref (not React state) so huge APKs don't sit in the
    // component tree and risk memory / re-render crashes.
    apkFileRef.current = file;
    setApkModal(prev => ({
      ...prev,
      fileName: name,
      fileSize: file.size || 0
    }));
  };

  const toastApiError = (err, fallback) => {
    const raw =
      (err && err.response && err.response.data && err.response.data.message) ||
      (err && err.message) ||
      fallback;
    const msg =
      typeof raw === "string"
        ? raw
        : raw && typeof raw.message === "string"
        ? raw.message
        : fallback;
    toaster.current && toaster.current.error(msg);
  };

  const uploadApkAndPush = async e => {
    if (e && e.preventDefault) e.preventDefault();

    const file = apkFileRef.current;
    const targets = apkModal.devices || [];
    if (!file) {
      toaster.current && toaster.current.error("Please select an APK file.");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".apk")) {
      toaster.current && toaster.current.error("Only .apk files are allowed.");
      return;
    }
    if (!targets.length) {
      toaster.current && toaster.current.error("No target tablets selected.");
      return;
    }

    setApkModal(prev => ({
      ...prev,
      uploading: true,
      progress: "Preparing upload…"
    }));
    setBulkBusy(true);

    try {
      // 1) Ask backend for a signed GCS PUT URL (Cloud Run max body is 32MB;
      //    APKs are ~50MB so we upload straight to storage from the browser).
      setApkModal(prev => ({ ...prev, progress: "Getting upload URL…" }));
      const urlRes = await Api.post(
        `admin/device/apk-upload-url?fileName=${encodeURIComponent(file.name)}`
      );
      if (urlRes.data?.status !== 1 && urlRes.data?.status !== "1") {
        throw new Error(
          typeof urlRes.data?.message === "string"
            ? urlRes.data.message
            : "Could not prepare APK upload. Storage signing failed."
        );
      }
      const payload = urlRes.data?.data || {};
      const uploadUrl = payload.uploadUrl;
      const downloadUrl = payload.downloadUrl;
      const contentType =
        payload.contentType || "application/vnd.android.package-archive";

      if (!uploadUrl || !downloadUrl) {
        throw new Error(
          "Could not prepare APK upload (missing storage URL). Try again in a minute."
        );
      }

      // 2) PUT file directly to GCS
      setApkModal(prev => ({
        ...prev,
        progress: `Uploading ${file.name}…`
      }));
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file
      });
      if (!putRes.ok) {
        throw new Error(
          `Storage upload failed (${putRes.status}). Check CORS / permissions.`
        );
      }

      // 3) Tell each tablet to install from the public download URL
      let success = 0;
      let failed = 0;
      for (let i = 0; i < targets.length; i += 1) {
        const device = targets[i];
        setApkModal(prev => ({
          ...prev,
          progress: `Pushing to tablet ${i + 1} of ${targets.length}…`
        }));
        try {
          let path = `admin/device/command?deviceTokenId=${device.deviceTokenId}&command=APP_UPDATE`;
          path += `&url=${encodeURIComponent(downloadUrl)}`;
          await Api.post(path);
          success += 1;
        } catch (err) {
          failed += 1;
        }
      }

      if (failed === 0) {
        toaster.current &&
          toaster.current.success(
            `APK update sent to ${success} tablet${success !== 1 ? "s" : ""}.`
          );
        clearSelection();
        resetApkModal();
        loadCommandLogs();
      } else {
        toaster.current &&
          toaster.current.error(
            `APK update: ${success} sent, ${failed} failed.`
          );
        setApkModal(prev => ({
          ...prev,
          uploading: false,
          progress: ""
        }));
        loadCommandLogs();
      }
    } catch (err) {
      toastApiError(err, "Failed to upload APK. Try again.");
      setApkModal(prev => ({
        ...prev,
        uploading: false,
        progress: ""
      }));
    } finally {
      setBulkBusy(false);
    }
  };

  const sendDeviceCommand = (device, command, url) => {
    if (!device.isOnline) {
      toaster.current && toaster.current.error("Device must be online.");
      return;
    }

    if (command === "REBOOT") {
      const ok = window.confirm(
        `Reboot ${device.deviceEmail ||
          "this tablet"}? It will restart in a few seconds.`
      );
      if (!ok) return;
    }

    if (command === "APP_UPDATE") {
      openApkModal([device]);
      return;
    }

    setCommandBusy(`${device.deviceTokenId}:${command}`);
    setOpenActionsId(null);

    let path = `admin/device/command?deviceTokenId=${
      device.deviceTokenId
    }&command=${encodeURIComponent(command)}`;
    if (url) path += `&url=${encodeURIComponent(url)}`;

    Api.post(path)
      .then(() => {
        const message =
          DEVICE_COMMAND_SUCCESS[command] || "Command sent to the tablet.";
        toaster.current && toaster.current.success(message);
        loadCommandLogs();
      })
      .catch(err => {
        const msg = err.response?.data?.message || `Failed to send ${command}`;
        toaster.current && toaster.current.error(msg);
      })
      .finally(() => setCommandBusy(null));
  };

  const pushApkToSelected = () => {
    const selected = devices.filter(d => selectedIds.includes(d.deviceTokenId));
    openApkModal(selected);
  };

  const viewScreen = device => {
    if (!device.isOnline) {
      toaster.current &&
        toaster.current.error("Device must be online to capture its screen.");
      return;
    }

    setScreenCapture({
      device,
      requestId: null,
      status: "pending",
      imageUrl: null,
      error: null
    });
    setScreenModal(true);

    Api.post(
      `admin/device/screen-capture/request?deviceTokenId=${device.deviceTokenId}`
    )
      .then(r => {
        const requestId = r.data?.data?.requestId;
        if (!requestId) {
          setScreenCapture(prev => ({
            ...prev,
            status: "failed",
            error: "Could not start screen capture"
          }));
          return;
        }
        setScreenCapture(prev => ({ ...prev, requestId, status: "pending" }));
        pollScreenCapture(requestId);
      })
      .catch(err => {
        const msg =
          err.response?.data?.message ||
          "Failed to request screen capture. Device may be missing a push token.";
        setScreenCapture(prev => ({ ...prev, status: "failed", error: msg }));
      });
  };

  const online = devices.filter(d => d.isOnline).length;
  const offline = devices.filter(d => !d.isOnline && d.lastSeenAt).length;
  const never = devices.filter(d => !d.lastSeenAt).length;
  const uniqueStores = new Set(devices.map(d => d.storeName).filter(Boolean))
    .size;
  const totalReceipts = devices.reduce((s, d) => s + (d.receiptsToday || 0), 0);
  const onlinePct =
    devices.length > 0 ? Math.round((online / devices.length) * 100) : 0;

  const fleetData = useMemo(
    () =>
      [
        { name: "Online", value: online },
        { name: "Offline", value: offline },
        { name: "Never", value: never }
      ].filter(d => d.value > 0),
    [online, offline, never]
  );

  const netData = useMemo(() => {
    const netCount = devices.reduce((acc, d) => {
      const k = d.networkType || "NONE";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(netCount).map(([name, value]) => ({ name, value }));
  }, [devices]);

  const storeOptions = useMemo(
    () =>
      Array.from(new Set(devices.map(d => d.storeName).filter(Boolean))).sort(),
    [devices]
  );

  const toggleStatus = name => {
    const key = name.toLowerCase();
    setStatusFilter(prev => (prev === key ? null : key));
    setNetworkFilter(null);
  };

  const toggleNetwork = name => {
    setNetworkFilter(prev => (prev === name ? null : name));
    setStatusFilter(null);
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setNetworkFilter(null);
    if (!lockedStore) setStoreFilter("");
    setSearch("");
  };

  const activeFleetFilter = statusFilter
    ? fleetData.find(d => d.name.toLowerCase() === statusFilter)?.name
    : null;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return devices.filter(d => {
      const matchSearch =
        !q ||
        [d.storeName, d.deviceEmail, d.branchName].some(v =>
          (v || "").toLowerCase().includes(q)
        );
      const matchStatus = !statusFilter || statusOf(d) === statusFilter;
      const matchNetwork =
        !networkFilter || (d.networkType || "NONE") === networkFilter;
      const matchStore = !storeFilter || d.storeName === storeFilter;
      return matchSearch && matchStatus && matchNetwork && matchStore;
    });
  }, [devices, search, statusFilter, networkFilter, storeFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const statusOrder = { online: 0, offline: 1, never: 2 };
    list.sort((a, b) => {
      if (sortBy === "receipts") {
        return (b.receiptsToday || 0) - (a.receiptsToday || 0);
      }
      if (sortBy === "lastSeen") {
        if (!a.lastSeenAt) return 1;
        if (!b.lastSeenAt) return -1;
        return new Date(b.lastSeenAt) - new Date(a.lastSeenAt);
      }
      return statusOrder[statusOf(a)] - statusOrder[statusOf(b)];
    });
    return list;
  }, [filtered, sortBy]);

  const onlineVisibleIds = useMemo(
    () => sorted.filter(d => d.isOnline).map(d => d.deviceTokenId),
    [sorted]
  );

  const selectedOnlineCount = useMemo(
    () => selectedIds.filter(id => onlineVisibleIds.includes(id)).length,
    [selectedIds, onlineVisibleIds]
  );

  const logDeviceOptions = useMemo(() => {
    const list = [...devices];
    list.sort((a, b) =>
      (a.deviceEmail || "").localeCompare(b.deviceEmail || "")
    );
    return list;
  }, [devices]);

  const allOnlineVisibleSelected =
    onlineVisibleIds.length > 0 &&
    onlineVisibleIds.every(id => selectedIds.includes(id));

  const toggleSelectAllOnlineVisible = () => {
    if (allOnlineVisibleSelected) {
      setSelectedIds(prev => prev.filter(id => !onlineVisibleIds.includes(id)));
    } else {
      setSelectedIds(prev =>
        Array.from(new Set([...prev, ...onlineVisibleIds]))
      );
    }
  };

  const activeFilterLabel =
    statusFilter?.toUpperCase() || networkFilter || storeFilter || null;

  return (
    <UserWrapper {...props}>
      <div className="gt-monitoring plr-15">
        <PageTitle
          title="sidebar.deviceMonitoring"
          className="plr-0"
          breadCrumb={
            isStore
              ? [
                  { name: "sidebar.store", path: "/store" },
                  { name: "sidebar.deviceMonitoring" }
                ]
              : [
                  { name: "sidebar.dashboard", path: "/dashboard" },
                  { name: "sidebar.deviceMonitoring" }
                ]
          }
        />

        <Toaster ref={toaster} />

        <div className="gt-monitor-stats">
          <MonitorStatCard
            label="Stores"
            value={uniqueStores}
            subtitle={`${uniqueStores} active merchant${
              uniqueStores !== 1 ? "s" : ""
            }`}
            icon="fas fa-store"
            variant="teal"
            active={!statusFilter && !networkFilter && !storeFilter}
            onClick={clearFilters}
          />
          <MonitorStatCard
            label="Devices Online"
            value={`${online}/${devices.length}`}
            subtitle={`${onlinePct}% of fleet live`}
            subVariant="ok"
            icon="fas fa-check-circle"
            variant="blue"
            active={statusFilter === "online"}
            onClick={() => toggleStatus("Online")}
          />
          <MonitorStatCard
            label="Offline"
            value={offline}
            subtitle={
              offline > 0 ? "Needs attention" : "All devices responding"
            }
            subVariant={offline > 0 ? "danger" : "ok"}
            icon="fas fa-exclamation-triangle"
            variant="rose"
            active={statusFilter === "offline"}
            onClick={() => toggleStatus("Offline")}
          />
          <MonitorStatCard
            label="Receipts Today"
            value={totalReceipts}
            subtitle={`Across ${devices.length} device${
              devices.length !== 1 ? "s" : ""
            }`}
            subVariant="muted"
            icon="fas fa-receipt"
            variant="amber"
            active={false}
            onClick={() => setSortBy("receipts")}
          />
        </div>

        <div className="gt-monitor-charts">
          <MonitorDonut
            title="Fleet health"
            subtitle="Click a segment to filter"
            data={
              fleetData.length ? fleetData : [{ name: "No data", value: 1 }]
            }
            colors={fleetData.length ? FLEET_COLORS : ["#cbd5e1"]}
            activeFilter={activeFleetFilter}
            onSegmentClick={fleetData.length ? toggleStatus : null}
          />
          <MonitorDonut
            title="Connectivity"
            subtitle="Network type breakdown"
            data={netData.length ? netData : [{ name: "No data", value: 1 }]}
            colors={netData.length ? NET_COLORS : ["#cbd5e1"]}
            activeFilter={networkFilter}
            onSegmentClick={netData.length ? toggleNetwork : null}
          />
          <div className="gt-chart-card">
            <div className="gt-chart-card__header">
              <h3 className="gt-chart-card__title">Today&apos;s activity</h3>
              <p className="gt-chart-card__subtitle">Live fleet snapshot</p>
            </div>
            <div className="gt-chart-card__body">
              <div className="gt-monitor-activity">
                {[
                  {
                    label: "Total devices",
                    value: devices.length,
                    color: "#0f172a"
                  },
                  { label: "Online now", value: online, color: "#10b981" },
                  { label: "Never connected", value: never, color: "#64748b" },
                  {
                    label: "Receipts today",
                    value: totalReceipts,
                    color: "#0ea5e9"
                  }
                ].map(r => (
                  <div key={r.label} className="gt-monitor-activity__row">
                    <span className="gt-monitor-activity__label">
                      {r.label}
                    </span>
                    <span
                      className="gt-monitor-activity__value"
                      style={{ color: r.color }}
                    >
                      {r.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          className={clsx(
            "gt-monitor-activity-log",
            !logsExpanded && "gt-monitor-activity-log--collapsed"
          )}
        >
          <div className="gt-monitor-activity-log__header">
            <button
              type="button"
              className="gt-monitor-activity-log__toggle"
              onClick={() => setLogsExpanded(prev => !prev)}
              aria-expanded={logsExpanded}
            >
              <i
                className={clsx(
                  "fas",
                  logsExpanded ? "fa-chevron-down" : "fa-chevron-right"
                )}
              />
              <h3>
                <i className="fas fa-stream" /> Device activity
              </h3>
              <span className="gt-monitor-activity-log__count">
                {commandLogs.length}
                {logsLimit < LOGS_MAX || canLoadMoreLogs
                  ? ` / ${logsLimit}`
                  : ""}
              </span>
            </button>
            <div className="gt-monitor-activity-log__header-actions">
              <button
                type="button"
                className="gt-monitor-bulk__clear"
                onClick={loadCommandLogs}
                disabled={logsLoading}
              >
                {logsLoading ? "Refreshing…" : "Refresh logs"}
              </button>
            </div>
          </div>

          {logsExpanded && (
            <>
              <div className="gt-monitor-activity-log__toolbar">
                <span className="gt-monitor-activity-log__meta">
                  Showing latest {Math.min(commandLogs.length, logsLimit)}
                  {logsDeviceFilter ? " for selected device" : ""}
                  {logsLimit < LOGS_MAX
                    ? ` (page size ${LOGS_PAGE_SIZE}, max ${LOGS_MAX})`
                    : ` (max ${LOGS_MAX})`}
                </span>
                <label className="gt-monitor-activity-log__filter">
                  <span>Device</span>
                  <select
                    value={logsDeviceFilter}
                    onChange={e => changeLogsDeviceFilter(e.target.value)}
                  >
                    <option value="">All devices</option>
                    {logDeviceOptions.map(d => (
                      <option key={d.deviceTokenId} value={d.deviceTokenId}>
                        {d.deviceEmail || `Device ${d.deviceTokenId}`}
                        {d.storeName ? ` · ${d.storeName}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {commandLogs.length === 0 ? (
                <div className="gt-monitor-activity-log__empty">
                  No remote commands yet. Push APK, remote login, reboot, and
                  View screen will appear here.
                </div>
              ) : (
                <>
                  <ul className="gt-monitor-activity-log__list">
                    {commandLogs.map(log => (
                      <li
                        key={log.id}
                        className={clsx(
                          "gt-monitor-activity-log__item",
                          `gt-monitor-activity-log__item--${(
                            log.status || "sent"
                          ).toLowerCase()}`
                        )}
                      >
                        <span
                          className={clsx(
                            "gt-monitor-activity-log__status",
                            `gt-monitor-activity-log__status--${(
                              log.status || "sent"
                            ).toLowerCase()}`
                          )}
                        >
                          {(log.status || "sent").toUpperCase()}
                        </span>
                        <div className="gt-monitor-activity-log__main">
                          <div className="gt-monitor-activity-log__cmd">
                            {log.command}
                            <span className="gt-monitor-activity-log__device">
                              {log.deviceEmail || `Device ${log.deviceTokenId}`}
                              {log.storeName ? ` · ${log.storeName}` : ""}
                            </span>
                          </div>
                          <div className="gt-monitor-activity-log__msg">
                            {log.message || "—"}
                          </div>
                        </div>
                        <div className="gt-monitor-activity-log__time">
                          {log.createdAt
                            ? moment
                                .utc(log.createdAt)
                                .local()
                                .format("DD MMM HH:mm")
                            : "—"}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="gt-monitor-activity-log__footer">
                    {canLoadMoreLogs ? (
                      <button
                        type="button"
                        className="gt-monitor-activity-log__load-more"
                        onClick={loadMoreLogs}
                        disabled={logsLoading}
                      >
                        {logsLoading
                          ? "Loading…"
                          : `Load more (+${LOGS_PAGE_SIZE})`}
                      </button>
                    ) : (
                      <span className="gt-monitor-activity-log__meta">
                        {logsLimit >= LOGS_MAX
                          ? `Showing up to ${LOGS_MAX} newest entries.`
                          : "All matching activities loaded."}
                      </span>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="gt-monitor-panel">
          <div className="gt-monitor-panel__header">
            <div>
              <h2 className="gt-monitor-panel__title">
                <i className="fas fa-satellite-dish" />
                Device fleet
                {activeFilterLabel && (
                  <span className="gt-monitor-filter-badge">
                    {activeFilterLabel}
                    <button
                      type="button"
                      onClick={clearFilters}
                      aria-label="Clear filter"
                    >
                      ✕
                    </button>
                  </span>
                )}
              </h2>
              <div className="gt-monitor-panel__meta">
                {sorted.length} of {devices.length} devices
                {lastRefreshed &&
                  ` · Updated ${moment(lastRefreshed).format("HH:mm:ss")}`}
                {" · Auto-refresh every 60s (background)"}
              </div>
            </div>

            <div className="gt-monitor-toolbar">
              <button
                type="button"
                className={clsx(
                  "gt-monitor-chip",
                  !statusFilter && !networkFilter && "gt-monitor-chip--active"
                )}
                onClick={clearFilters}
              >
                All
              </button>
              <button
                type="button"
                className={clsx(
                  "gt-monitor-chip gt-monitor-chip--online",
                  statusFilter === "online" && "gt-monitor-chip--active"
                )}
                onClick={() => toggleStatus("Online")}
              >
                Online ({online})
              </button>
              <button
                type="button"
                className={clsx(
                  "gt-monitor-chip gt-monitor-chip--offline",
                  statusFilter === "offline" && "gt-monitor-chip--active"
                )}
                onClick={() => toggleStatus("Offline")}
              >
                Offline ({offline})
              </button>
              <button
                type="button"
                className={clsx(
                  "gt-monitor-chip gt-monitor-chip--never",
                  statusFilter === "never" && "gt-monitor-chip--active"
                )}
                onClick={() => toggleStatus("Never")}
              >
                Never ({never})
              </button>

              {lockedStore ? (
                <span className="gt-monitor-store-badge">
                  <i className="fa fa-store" />
                  {lockedStore}
                </span>
              ) : (
                <select
                  className="gt-monitor-select"
                  value={storeFilter}
                  onChange={e => setStoreFilter(e.target.value)}
                >
                  <option value="">All stores</option>
                  {storeOptions.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}

              <select
                className="gt-monitor-select"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="status">Sort: status</option>
                <option value="lastSeen">Sort: last seen</option>
                <option value="receipts">Sort: receipts</option>
              </select>

              <input
                type="text"
                className="gt-monitor-input"
                placeholder="Search email / branch..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />

              <button
                type="button"
                className="gt-monitor-btn"
                onClick={load}
                disabled={loading}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="gt-monitor-bulk">
              <label className="gt-monitor-bulk__select-all">
                <input
                  type="checkbox"
                  checked={allOnlineVisibleSelected}
                  onChange={toggleSelectAllOnlineVisible}
                />
                Select all online in view ({onlineVisibleIds.length})
              </label>
              <span className="gt-monitor-bulk__count">
                {selectedIds.length} selected
                {selectedOnlineCount < selectedIds.length
                  ? ` · ${selectedOnlineCount} online`
                  : ""}
              </span>
              <button
                type="button"
                className="gt-monitor-btn"
                disabled={bulkBusy || selectedOnlineCount === 0}
                onClick={pushApkToSelected}
              >
                {bulkBusy
                  ? "Pushing…"
                  : `Push APK to ${selectedOnlineCount || "selected"}`}
              </button>
              <button
                type="button"
                className="gt-monitor-bulk__clear"
                disabled={bulkBusy}
                onClick={clearSelection}
              >
                Clear
              </button>
            </div>
          )}

          {sorted.length === 0 ? (
            <div className="gt-monitor-empty">
              <i
                className="fas fa-tablet-alt"
                style={{
                  fontSize: 32,
                  marginBottom: 12,
                  display: "block",
                  opacity: 0.4
                }}
              />
              No devices match your filters
            </div>
          ) : (
            <div className="gt-monitor-list">
              {selectedIds.length === 0 && onlineVisibleIds.length > 0 && (
                <div className="gt-monitor-bulk gt-monitor-bulk--hint">
                  <label className="gt-monitor-bulk__select-all">
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={toggleSelectAllOnlineVisible}
                    />
                    Select online devices to push APK update in bulk
                  </label>
                </div>
              )}
              {sorted.map(d => {
                const st = statusOf(d);
                const isSelected = selectedIds.includes(d.deviceTokenId);
                return (
                  <div
                    key={d.deviceTokenId}
                    className={clsx(
                      "gt-monitor-device",
                      `gt-monitor-device--${st}`,
                      isSelected && "gt-monitor-device--selected"
                    )}
                  >
                    <label className="gt-monitor-device__check">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!d.isOnline}
                        title={
                          d.isOnline
                            ? "Select for bulk APK push"
                            : "Offline devices cannot receive APK updates"
                        }
                        onChange={() => toggleDeviceSelected(d.deviceTokenId)}
                      />
                    </label>
                    <span
                      className={clsx(
                        "gt-monitor-device__status",
                        `gt-monitor-device__status--${st}`
                      )}
                    />
                    <div className="gt-monitor-device__main">
                      <div className="gt-monitor-device__label">
                        Login email
                      </div>
                      <div className="gt-monitor-device__email">
                        {d.deviceEmail || "—"}
                        <span className="gt-monitor-device__id">
                          ID {d.deviceTokenId}
                        </span>
                      </div>
                      <div className="gt-monitor-device__meta">
                        {[d.storeName, d.branchName]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="gt-monitor-device__col">
                      <div className="gt-monitor-device__label">Screen</div>
                      <ScreenPill state={d.screenState} />
                    </div>
                    <div className="gt-monitor-device__col">
                      <div className="gt-monitor-device__label">Signal</div>
                      <SignalBars type={d.networkType} />
                    </div>
                    <div className="gt-monitor-device__col">
                      <div className="gt-monitor-device__label">Receipts</div>
                      <div className="gt-monitor-device__value gt-monitor-device__value--receipts">
                        {d.receiptsToday ?? 0}
                      </div>
                    </div>
                    <div className="gt-monitor-device__col">
                      <div className="gt-monitor-device__label">Version</div>
                      <div className="gt-monitor-device__value">
                        {d.appVersion || "—"}
                      </div>
                    </div>
                    <div className="gt-monitor-device__col">
                      <div className="gt-monitor-device__label">Last seen</div>
                      <div
                        className={clsx(
                          "gt-monitor-device__value",
                          st === "online" && "gt-monitor-device__value--online",
                          st === "offline" &&
                            "gt-monitor-device__value--offline"
                        )}
                      >
                        {d.lastSeenAt
                          ? moment
                              .utc(d.lastSeenAt)
                              .local()
                              .format("HH:mm")
                          : "—"}
                      </div>
                      {d.lastSeenMinutesAgo != null && (
                        <div className="gt-monitor-device__sub">
                          {d.lastSeenMinutesAgo}m ago
                        </div>
                      )}
                    </div>
                    <div className="gt-monitor-device__col gt-monitor-device__col--actions">
                      <button
                        type="button"
                        className="gt-monitor-view-screen"
                        disabled={st !== "online" || screenModal}
                        onClick={() => viewScreen(d)}
                        title={
                          st === "online"
                            ? "Capture what is on the tablet now"
                            : "Device must be online"
                        }
                      >
                        <i className="fas fa-desktop" /> View screen
                      </button>
                      <div className="gt-monitor-actions">
                        <button
                          type="button"
                          className="gt-monitor-actions__toggle"
                          disabled={st !== "online" || commandBusy}
                          onClick={e => {
                            e.stopPropagation();
                            setOpenActionsId(prev =>
                              prev === d.deviceTokenId ? null : d.deviceTokenId
                            );
                          }}
                          title="Device Owner remote commands"
                        >
                          <i className="fas fa-cog" />
                        </button>
                        {openActionsId === d.deviceTokenId && (
                          <div
                            className="gt-monitor-actions__menu"
                            onClick={e => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              disabled={
                                commandBusy ===
                                `${d.deviceTokenId}:REMOTE_LOGIN`
                              }
                              onClick={() =>
                                sendDeviceCommand(d, "REMOTE_LOGIN")
                              }
                            >
                              Remote login
                            </button>
                            <button
                              type="button"
                              disabled={
                                commandBusy === `${d.deviceTokenId}:OPEN_LOGIN`
                              }
                              onClick={() => sendDeviceCommand(d, "OPEN_LOGIN")}
                            >
                              Show login screen
                            </button>
                            <button
                              type="button"
                              disabled={
                                commandBusy === `${d.deviceTokenId}:SET_HOME`
                              }
                              onClick={() => sendDeviceCommand(d, "SET_HOME")}
                            >
                              Set as home app
                            </button>
                            <button
                              type="button"
                              disabled={
                                commandBusy === `${d.deviceTokenId}:APPLY_KIOSK`
                              }
                              onClick={() =>
                                sendDeviceCommand(d, "APPLY_KIOSK")
                              }
                            >
                              Apply kiosk lock
                            </button>
                            <button
                              type="button"
                              disabled={
                                commandBusy === `${d.deviceTokenId}:APP_UPDATE`
                              }
                              onClick={() => sendDeviceCommand(d, "APP_UPDATE")}
                            >
                              Push APK update
                            </button>
                            <button
                              type="button"
                              className="gt-monitor-actions__danger"
                              disabled={
                                commandBusy === `${d.deviceTokenId}:REBOOT`
                              }
                              onClick={() => sendDeviceCommand(d, "REBOOT")}
                            >
                              Reboot tablet
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {screenModal && (
        <div className="gt-screen-modal-backdrop" onClick={closeScreenModal}>
          <div
            className="gt-screen-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-label="Device screen preview"
          >
            <div className="gt-screen-modal__header">
              <div>
                <h3 className="gt-screen-modal__title">View screen</h3>
                <p className="gt-screen-modal__subtitle">
                  {screenCapture.device?.deviceEmail || "Device"}{" "}
                  {screenCapture.device?.storeName
                    ? `· ${screenCapture.device.storeName}`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                className="gt-screen-modal__close"
                onClick={closeScreenModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="gt-screen-modal__body">
              {screenCapture.status === "pending" && (
                <div className="gt-screen-modal__loading">
                  <i className="fas fa-spinner fa-spin" />
                  <p>Requesting snapshot from tablet…</p>
                  <span className="gt-screen-modal__hint">
                    Shows the GreenTill screen. If the app is in the background,
                    it will be brought to the front first (usually 5–15
                    seconds).
                  </span>
                </div>
              )}
              {screenCapture.status === "ready" && screenCapture.imageUrl && (
                <img
                  src={screenCapture.imageUrl}
                  alt="Tablet screen"
                  className="gt-screen-modal__image"
                />
              )}
              {(screenCapture.status === "failed" ||
                screenCapture.status === "expired") && (
                <div className="gt-screen-modal__error">
                  <i className="fas fa-exclamation-circle" />
                  <p>
                    {screenCapture.error ||
                      "Could not retrieve a screen snapshot."}
                  </p>
                  {screenCapture.device && (
                    <button
                      type="button"
                      className="gt-monitor-view-screen"
                      onClick={() => viewScreen(screenCapture.device)}
                    >
                      Try again
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {apkModal.open && (
        <div
          className="gt-screen-modal-backdrop"
          onClick={apkModal.uploading ? undefined : closeApkModal}
        >
          <form
            className="gt-apk-modal"
            onClick={e => e.stopPropagation()}
            onSubmit={uploadApkAndPush}
            role="dialog"
            aria-label="Push APK update"
          >
            <div className="gt-apk-modal__header">
              <div>
                <h3 className="gt-apk-modal__title">Push APK update</h3>
                <p className="gt-apk-modal__subtitle">
                  Select an APK from your computer. It will upload to GreenTill
                  storage, then install on{" "}
                  {(apkModal.devices || []).length === 1
                    ? (apkModal.devices[0] &&
                        apkModal.devices[0].deviceEmail) ||
                      "1 tablet"
                    : `${(apkModal.devices || []).length} online tablets`}
                  .
                </p>
              </div>
              <button
                type="button"
                className="gt-screen-modal__close"
                onClick={closeApkModal}
                disabled={apkModal.uploading}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="gt-apk-modal__body">
              <label className="gt-apk-modal__file">
                <input
                  ref={apkInputRef}
                  type="file"
                  accept=".apk,application/vnd.android.package-archive"
                  disabled={apkModal.uploading}
                  onChange={onApkFileChosen}
                />
                <span>
                  {apkModal.fileName
                    ? `${apkModal.fileName} (${Math.max(
                        1,
                        Math.round(apkModal.fileSize / (1024 * 1024))
                      )} MB)`
                    : "Choose APK file…"}
                </span>
              </label>
              {apkModal.progress && (
                <div className="gt-apk-modal__progress">
                  {apkModal.progress}
                </div>
              )}
              {(apkModal.devices || []).length > 1 && (
                <ul className="gt-apk-modal__targets">
                  {apkModal.devices.slice(0, 8).map(d => (
                    <li key={d.deviceTokenId}>
                      {d.deviceEmail || `Device ${d.deviceTokenId}`}
                      {d.storeName ? ` · ${d.storeName}` : ""}
                    </li>
                  ))}
                  {apkModal.devices.length > 8 && (
                    <li>…and {apkModal.devices.length - 8} more</li>
                  )}
                </ul>
              )}
            </div>
            <div className="gt-apk-modal__footer">
              <button
                type="button"
                className="gt-monitor-bulk__clear"
                onClick={closeApkModal}
                disabled={apkModal.uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="gt-monitor-btn"
                disabled={apkModal.uploading || !apkModal.fileName}
              >
                {apkModal.uploading
                  ? "Uploading & pushing…"
                  : `Upload & push to ${(apkModal.devices || []).length}`}
              </button>
            </div>
          </form>
        </div>
      )}
    </UserWrapper>
  );
};

const mapStateToProps = state => ({
  ...state.themeChanger,
  isStore: state.storeID?.isStore,
  authData: {
    adminType: state.auth.adminType,
    storeName: state.auth.storeName
  },
  storeData: {
    storeId: state.storeID?.storeId,
    storeName: state.storeID?.storeName
  }
});

export default connect(mapStateToProps)(DeviceMonitoring);
